/** @jsxImportSource @emotion/react */
import {
  Table as MuiTable,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import { isNumber, isString } from 'lodash';
import React, {
  ReactNode,
  useCallback,
  useMemo,
  useState,
  KeyboardEvent,
} from 'react';
import { Checkbox, createStyles, Icon, Typography, useTheme } from '../..';

type CellKey = string;
type CellValue = string | number | ReactNode | undefined;
type Row = {
  value: Record<CellKey, CellValue>;
  disabled?: boolean;
  id?: string;
};

interface Header {
  label: string;
  cellKey: CellKey;
  sort?: boolean;
}

export interface Props {
  headers: Header[];
  rows: Row[];
  onRowClick?: (row: Row['value']) => void;
  disableOnRowClick?: boolean;
  onSort?: (cellKey: CellKey, asc: boolean) => void;
  selected?: Set<string>;
  updateSelected?: React.Dispatch<React.SetStateAction<Set<string>>>;
}

const compareRowValues = (cell1: CellValue, cell2: CellValue) => {
  if (isString(cell1) && isString(cell2)) return cell1.localeCompare(cell2);
  if (isNumber(cell1) && isNumber(cell2)) return cell1 - cell2;
  if (cell1 === undefined && cell2 !== undefined) return 1;
  if (cell2 === undefined && cell1 !== undefined) return -1;
  return 0;
};

const isEnterClicked = (e: KeyboardEvent) => {
  return e.key === 'Enter' || e.key === ' ';
};

/**
 * A table to visualize data.
 * @param onSort If given, overrides the default sorting behavior
 */
export const Table = ({
  headers,
  rows,
  onRowClick,
  disableOnRowClick,
  onSort,
  selected,
  updateSelected,
}: Props) => {
  const { palette, spacing } = useTheme();
  const styles = useStyles();
  const [sortBy, setSortBy] = useState<{ cellKey: CellKey; asc: boolean }>();

  const sortedRows = useMemo(() => {
    if (!sortBy) return rows;
    if (onSort) {
      return rows;
    }
    const sorted = [...rows].sort(
      (
        { value: { [sortBy.cellKey]: r1 } },
        { value: { [sortBy.cellKey]: r2 } }
      ) => {
        return compareRowValues(r1, r2);
      }
    );
    if (!sortBy.asc) {
      sorted.reverse();
    }
    return sorted;
  }, [rows, sortBy, onSort]);

  const handleSortClick = useCallback(
    (cellKey: CellKey, sort: boolean) => {
      if (sort) {
        const asc = sortBy?.cellKey === cellKey ? !sortBy.asc : true;
        setSortBy({
          cellKey,
          asc,
        });
        onSort && onSort(cellKey, asc);
      }
    },
    [sortBy, onSort]
  );

  const handleRowClick = (row: Row['value']) => {
    if (
      onRowClick &&
      (window.getSelection() === null ||
        window.getSelection()?.toString() === '')
    ) {
      onRowClick(row);
    }
  };

  const handleRowKeyDown = (e: React.KeyboardEvent, row: Row['value']) => {
    if (isEnterClicked(e)) {
      e.preventDefault();
      if (onRowClick) {
        onRowClick(row);
      }
    }
  };

  const handleHeaderKeyDown = (
    e: React.KeyboardEvent,
    cellKey: CellKey,
    sort: boolean
  ) => {
    if (isEnterClicked(e)) {
      e.preventDefault();
      handleSortClick(cellKey, sort);
    }
  };

  return (
    <MuiTable css={styles.table}>
      <TableHead>
        <TableRow>
          {updateSelected && selected != undefined && (
            <TableCell
              key={'check-all'}
              onClick={() =>
                updateSelected((prev) => {
                  if (prev.size > 0) return new Set();
                  return new Set(
                    rows.map((row) => row.id ?? '').filter((id) => id !== '')
                  );
                })
              }
              tabIndex={0}
              onKeyDown={(e) => {
                if (isEnterClicked(e)) {
                  e.preventDefault();
                  updateSelected((prev) => {
                    if (prev.size > 0) return new Set();
                    return new Set(
                      rows.map((row) => row.id ?? '').filter((id) => id !== '')
                    );
                  });
                }
              }}
            >
              <div css={styles.thContainer}>
                <Checkbox checked={selected.size === rows.length} />
              </div>
            </TableCell>
          )}
          {headers.map(({ label, cellKey, sort = true }) => (
            <TableCell
              key={label}
              onClick={() => handleSortClick(cellKey, sort)}
              onKeyDown={(e) => handleHeaderKeyDown(e, cellKey, sort)}
              tabIndex={sort ? 0 : -1}
              css={sort && styles.th}
            >
              <div css={styles.thContainer}>
                <div css={styles.thTitle}>
                  <Typography
                    variant="bodyMedium"
                    color={
                      sortBy?.cellKey === cellKey
                        ? palette.black
                        : palette.grey.x500
                    }
                  >
                    {label}
                  </Typography>
                </div>
                {sort && (
                  <div
                    css={[
                      {
                        ...(sortBy?.cellKey === cellKey && !sortBy.asc
                          ? styles.rotateUp
                          : styles.rotateDown),
                      },
                      [
                        {
                          ...(sortBy?.cellKey === cellKey
                            ? styles.fillDark
                            : styles.fillLight),
                        },
                      ],
                    ]}
                  >
                    <Icon name="chevron" size={spacing.xxs} />
                  </div>
                )}
              </div>
            </TableCell>
          ))}
        </TableRow>
      </TableHead>
      <TableBody>
        {sortedRows.map(({ value, disabled, id }, idx) => (
          <TableRow
            key={`row_${idx}`}
            onClick={() => handleRowClick(value)}
            onKeyDown={(e) => handleRowKeyDown(e, value)}
            tabIndex={onRowClick && !disabled ? 0 : -1}
            css={[
              onRowClick && styles.clickableRow,
              disabled && [
                { ...(disableOnRowClick ? styles.disableClick : {}) },
                styles.disabled,
              ],
            ]}
          >
            {updateSelected && selected != undefined && (
              <TableCell
                key={`check-${idx}`}
                onClick={(e) => {
                  e.stopPropagation();
                  if (id === undefined) return;
                  updateSelected((prev) => {
                    const next = new Set(prev);
                    if (selected.has(id)) {
                      next.delete(id);
                    } else {
                      next.add(id);
                    }
                    return next;
                  });
                }}
                onKeyDown={(e) => {
                  e.stopPropagation();
                  if (isEnterClicked(e)) {
                    e.preventDefault();
                    if (id === undefined) return;
                    updateSelected((prev) => {
                      const next = new Set(prev);
                      if (selected.has(id)) {
                        next.delete(id);
                      } else {
                        next.add(id);
                      }
                      return next;
                    });
                  }
                }}
                tabIndex={0}
              >
                <Checkbox checked={selected.has(id ?? '')} />
              </TableCell>
            )}
            {headers.map(({ cellKey }) => (
              <TableCell key={cellKey}>{value[cellKey]}</TableCell>
            ))}
          </TableRow>
        ))}
      </TableBody>
    </MuiTable>
  );
};

const useStyles = createStyles(({ palette, spacing, shape }) => ({
  table: {
    margin: 0,
    padding: 0,
    background: palette.white,
    minWidth: spacing.xxxl,
    borderRadius: shape.borderRadius,
    'tr:last-of-type': {
      td: {
        borderBottom: 'none',
      },
    },
  },
  th: {
    cursor: 'pointer',
  },
  thContainer: { display: 'flex' },
  thTitle: { paddingRight: spacing.xxxs, textAlign: 'center' },
  rotateUp: {
    transform: 'rotate(90deg)',
    transformOrigin: '45% 40%',
  },
  rotateDown: { transform: 'rotate(-90deg)' },
  clickableRow: {
    cursor: 'pointer',
    ':hover': {
      background: palette.grey.x50,
    },
    ':focus': {
      background: palette.grey.x50,
      outline: 'none',
      boxShadow: `0 0 0 2px ${palette.primary.x400}`,
    },
    ':focus-visible': {
      background: palette.grey.x50,
      outline: 'none',
      boxShadow: `0 0 0 2px ${palette.primary.x400}`,
    },
  },
  fillLight: {
    svg: {
      fill: palette.grey.x500,
    },
  },
  fillDark: {
    svg: {
      fill: palette.black,
    },
  },
  disabled: {
    opacity: '0.5',
  },
  disableClick: {
    pointerEvents: 'none',
  },
}));
